
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { saveAs } from "file-saver";

import Loader from "../../components/Loader";
import CardCanvas from "./CardCanvas";
import { LogoBox } from "./BlockEditor";
import {
    BACK_PARTS,
    EMPTY_CARD_DRAFT,
    FRONT_PARTS,
    PART_LABELS,
    PART_SHOW,
    buildCard,
    createCard,
    deleteCard,
    duplicateCard,
    fetchCardFile,
    getCard,
    getCardMeta,
    previewCard,
    toCardDraft,
    updateCard,
} from "./printCardApi";
import {
    EMPTY_PROFILE_DRAFT,
    createProfile,
    deleteProfileLogo,
    getProfile,
    listAssets,
    listProfiles,
    readApiError,
    toProfileDraft,
    updateProfile,
    uploadProfileLogo,
} from "./printSignApi";
import {
    AssetPicker,
    Choice,
    ColorsEditor,
    Field,
    ImageAdjust,
    MAX_IMAGE_BYTES,
    PartRow,
    Section,
    StringList,
    Switch,
    formatBytes,
    formatDateTime,
    sameDraft,
} from "./signUi";
import { ActionsMenu, PageMessage, SignsPage, StatusBadge } from "./signPage";

/*
 * Редактор визитки: слева настройки, справа обе стороны.
 * На картинках куски (логотип, надпись, ФИО, контакты, QR) двигаются мышкой, колесо — размер.
 * Данные фирмы — из профиля бренда (правятся тут же и сохраняются в профиль, как у вывесок).
 */

export const CARDS_ROOT = "/admin/print-cards";

const BLEEDS = [
    { value: 0, label: "Нет" },
    { value: 2, label: "2 мм", hint: "обычно" },
    { value: 3, label: "3 мм", hint: "типография" },
];
const ORIENTATIONS = [
    { value: "horizontal", label: "Горизонтальная" },
    { value: "vertical", label: "Вертикальная" },
];
// Что входит в «Контакты» (сам кусок прячется глазом).
const CONTACT_SHOW = [["phones", "Телефоны"], ["emails", "Почты"], ["addresses", "Адреса"], ["site", "Сайт"], ["hours", "Режим работы"]];
const TEMPLATE_NAMES = ["", "Новый профиль"];
const TEMPLATE_BRAND = "Название фирмы";
const AUTO_PREVIEW_MS = 800;

const toSize = (text) => {
    const number = Number(String(text).replace(/\D/g, "").slice(0, 3));
    return Number.isFinite(number) ? number : 0;
};

/** Предпросмотр обеих сторон: перерисовывается сам после паузы в правках. */
const useCardPreview = ({ request, deps, enabled }) => {
    const [state, setState] = useState({ front: null, back: null, warnings: [], loading: false, error: "" });
    const [auto, setAuto] = useState(true);
    const seqRef = useRef(0);
    const requestRef = useRef(request);
    requestRef.current = request;
    const firstRef = useRef(true);

    const refresh = useCallback(async () => {
        const seq = seqRef.current + 1;
        seqRef.current = seq;
        setState((current) => ({ ...current, loading: true, error: "" }));
        try {
            const result = await requestRef.current();
            if (seq !== seqRef.current) return;
            setState({ front: result.front, back: result.back, warnings: result.warnings || [], loading: false, error: "" });
        } catch (error) {
            const text = await readApiError(error, "Не удалось нарисовать визитку");
            if (seq === seqRef.current) setState((current) => ({ ...current, loading: false, error: text }));
        }
    }, []);

    const key = JSON.stringify(deps);
    useEffect(() => {
        if (!enabled || (!auto && !firstRef.current)) return undefined;
        const delay = firstRef.current ? 0 : AUTO_PREVIEW_MS;
        firstRef.current = false;
        const timer = window.setTimeout(refresh, delay);
        return () => window.clearTimeout(timer);
    }, [auto, enabled, key, refresh]);

    return { ...state, auto, setAuto, refresh };
};

/** Шаг стрелок: доля визитки (1% — около миллиметра). */
const NUDGE = 0.01;
const nudge = (value, delta) => Math.round(Math.min(1, Math.max(-1, Number(value || 0) + delta)) * 1000) / 1000;

/** Куски стороны: размер, стрелки ← → ↑ ↓, глаз (показать / скрыть) и «как в раскладке». */
const PartsEditor = ({ side, keys, parts, show, onChange, onShow, locked, unavailable = {} }) => {
    const changed = keys.some((key) => parts[key].s !== 1 || parts[key].x || parts[key].y);
    return (
        <div className="printSigns__adjust">
            <div className="printCatalogs__label">Размер, место и показать (100% — как в раскладке)</div>
            {keys.map((key) => (
                <PartRow
                    key={key}
                    label={PART_LABELS[key]}
                    scale={parts[key].s}
                    min={0.3}
                    max={3}
                    visible={show[PART_SHOW[key]] !== false}
                    unavailable={unavailable[key] || ""}
                    disabled={locked}
                    onScale={(s) => onChange(side, key, { s })}
                    onNudge={(dx, dy) => onChange(side, key, { x: nudge(parts[key].x, dx * NUDGE), y: nudge(parts[key].y, dy * NUDGE) })}
                    onToggle={(visible) => onShow(side, PART_SHOW[key], visible)}
                />
            ))}
            <div className="printSigns__headerReset">
                <span>Стрелки двигают на шаг; на картинке всё тянется мышкой, колесо — больше / меньше.</span>
                <button type="button" className="printSigns__add" disabled={locked || !changed}
                    onClick={() => keys.forEach((key) => onChange(side, key, { s: 1, x: 0, y: 0 }))}>
                    Как в раскладке
                </button>
            </div>
        </div>
    );
};

const CardEditorView = ({ cardId, onCreated }) => {
    const navigate = useNavigate();
    const [meta, setMeta] = useState(null);
    const [profiles, setProfiles] = useState([]);
    const [assets, setAssets] = useState([]);
    const [card, setCard] = useState(null);
    const [draft, setDraft] = useState(EMPTY_CARD_DRAFT);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [profile, setProfile] = useState(null);
    const [profileDraft, setProfileDraft] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [busy, setBusy] = useState("");
    const [message, setMessage] = useState(null);
    const [selected, setSelected] = useState({ side: "", key: "" });
    const [frames, setFrames] = useState(true);
    const mountedRef = useRef(true);
    const profileKeyRef = useRef(undefined);
    const leaveRef = useRef(false);

    useEffect(() => () => {
        mountedRef.current = false;
    }, []);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const [nextMeta, nextProfiles, nextAssets, nextCard] = await Promise.all([
                    getCardMeta(),
                    listProfiles(),
                    listAssets(),
                    cardId !== "new" ? getCard(cardId) : Promise.resolve(null),
                ]);
                if (!alive) return;
                setMeta(nextMeta);
                setProfiles(nextProfiles);
                setAssets(nextAssets);
                if (nextCard) {
                    setCard(nextCard);
                    setDraft(toCardDraft(nextCard));
                } else {
                    setDraft({ ...EMPTY_CARD_DRAFT, profileId: nextProfiles[0]?._id || null });
                }
            } catch (error) {
                if (alive) setLoadError(await readApiError(error, "Визитка не открылась"));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const template = useMemo(() => toProfileDraft({ ...EMPTY_PROFILE_DRAFT, ...(meta?.profileTemplate || {}) }), [meta]);
    const savedCard = useMemo(() => (card ? toCardDraft(card) : null), [card]);
    const savedProfile = useMemo(() => (profile ? toProfileDraft(profile) : template), [profile, template]);
    const cardDirty = !savedCard || !sameDraft(draft, savedCard);
    const profileDirty = !!profileDraft && !sameDraft(profileDraft, savedProfile);
    const dirty = cardDirty || profileDirty || !profile;
    const locked = !!busy;

    // Профиль: выбранный — с сервера, не выбран — заготовка.
    const wantedProfileId = loading || !meta ? undefined : draft.profileId || null;
    useEffect(() => {
        if (wantedProfileId === undefined || wantedProfileId === profileKeyRef.current) return undefined;
        profileKeyRef.current = wantedProfileId;
        if (!wantedProfileId) {
            setProfile(null);
            setProfileDraft(template);
            return undefined;
        }
        let alive = true;
        setProfileLoading(true);
        getProfile(wantedProfileId)
            .then((next) => {
                if (!alive) return;
                setProfile(next);
                setProfileDraft(toProfileDraft(next));
            })
            .catch(async (error) => {
                if (!alive) return;
                profileKeyRef.current = null;
                setProfile(null);
                setProfileDraft(template);
                setDraft((current) => ({ ...current, profileId: null }));
                setMessage({ type: "error", text: await readApiError(error, "Профиль не открылся — взята заготовка") });
            })
            .finally(() => alive && setProfileLoading(false));
        return () => {
            alive = false;
        };
    }, [wantedProfileId, template]);

    /* ---------- Правки ---------- */

    const patch = useCallback((changes) => {
        setDraft((current) => ({ ...current, ...changes }));
        setMessage(null);
    }, []);
    const patchNested = useCallback((key, changes) => {
        setDraft((current) => ({ ...current, [key]: { ...current[key], ...changes } }));
        setMessage(null);
    }, []);
    const patchSide = useCallback((sideKey, changes) => {
        setDraft((current) => ({ ...current, [sideKey]: { ...current[sideKey], ...changes } }));
        setMessage(null);
    }, []);
    const patchShow = (sideKey, key, value) => setDraft((current) => ({
        ...current,
        [sideKey]: { ...current[sideKey], show: { ...current[sideKey].show, [key]: value } },
    }));
    const patchPart = useCallback((sideKey, key, changes) => {
        setDraft((current) => ({
            ...current,
            [sideKey]: {
                ...current[sideKey],
                parts: { ...current[sideKey].parts, [key]: { ...current[sideKey].parts[key], ...changes } },
            },
        }));
        setMessage(null);
    }, []);
    const patchProfile = useCallback((changes) => {
        setProfileDraft((current) => ({ ...current, ...changes }));
        setMessage(null);
    }, []);

    const upsertProfile = (next) => setProfiles((current) => (
        current.some((item) => item._id === next._id) ? current.map((item) => (item._id === next._id ? { ...item, ...next } : item)) : [next, ...current]
    ));

    const changeProfile = (nextId) => {
        const id = nextId || null;
        if (id === (draft.profileId || null)) return;
        if (profileDirty && !window.confirm("Несохранённые правки данных фирмы пропадут. Переключить профиль?")) return;
        patch({ profileId: id });
    };

    /* ---------- Картинки ---------- */

    const preview = useCardPreview({
        enabled: !loading && !profileLoading && !!profileDraft,
        deps: { draft, profileDraft, profileId: profile?._id, logo: profile?.logoUpdatedAt },
        request: () => previewCard({ cardId: card?._id || "", card: draft, profileId: profile?._id || "", profile: profileDraft, width: 900 }),
    });

    /* ---------- Сохранение ---------- */

    const profileName = () => {
        if (!TEMPLATE_NAMES.includes(String(profileDraft.name || "").trim())) return profileDraft.name.trim();
        const brand = String(profileDraft.brandName || "").trim();
        if (brand && brand !== TEMPLATE_BRAND) return brand;
        return "Профиль бренда";
    };

    const persistProfile = async () => {
        if (!profile) {
            const created = await createProfile({ ...profileDraft, name: profileName() });
            profileKeyRef.current = created._id;
            if (!mountedRef.current) return created;
            setDraft((current) => ({ ...current, profileId: created._id }));
            setProfile(created);
            setProfileDraft(toProfileDraft(created));
            upsertProfile(created);
            return created;
        }
        if (!profileDirty) return profile;
        const updated = await updateProfile(profile._id, profileDraft);
        if (!mountedRef.current) return updated;
        setProfile(updated);
        setProfileDraft(toProfileDraft(updated));
        upsertProfile(updated);
        return updated;
    };

    const persist = async () => {
        const savedProfileDoc = await persistProfile();
        const payload = { ...draft, profileId: savedProfileDoc._id, name: draft.name.trim() || draft.person.name.trim() || savedProfileDoc.brandName || "Визитка" };
        const next = card ? await updateCard(card._id, payload) : await createCard(payload);
        if (!mountedRef.current) return next;
        setCard(next);
        setDraft(toCardDraft(next));
        if (!card) onCreated(next._id);
        return next;
    };

    const run = async (key, task, errorText) => {
        if (busy) return;
        setBusy(key);
        setMessage(null);
        try {
            await task();
        } catch (error) {
            if (mountedRef.current) setMessage({ type: "error", text: await readApiError(error, errorText) });
        } finally {
            if (mountedRef.current) setBusy("");
        }
    };

    const save = () => run("save", async () => {
        const hadProfile = !!profile;
        const changedProfile = profileDirty;
        await persist();
        if (!mountedRef.current) return;
        setMessage({
            type: "success",
            text: !hadProfile
                ? "Сохранено. Создан профиль бренда — его можно выбрать и в других визитках и вывесках."
                : `Сохранено.${changedProfile ? " Данные фирмы обновились во всех визитках и вывесках с этим профилем." : ""}`,
        });
    }, "Не удалось сохранить");

    const build = () => run("build", async () => {
        const current = dirty || !card ? await persist() : card;
        const next = await buildCard(current._id);
        if (!mountedRef.current) return;
        setCard(next);
        const last = next.files[0];
        setMessage({
            type: last?.warnings?.length ? "info" : "success",
            text: last?.warnings?.length ? `Собрано. ${last.warnings.join(" ")}` : "Готово: PDF для типографии, лист A4 и PNG — внизу слева, в «Готовых файлах».",
        });
    }, "Не удалось собрать визитку");

    const download = (file, format) => card && run(`download:${file._id}:${format}`, async () => {
        const name = `${(card.name || "Визитка").replace(/[\\/:*?"<>|]+/g, " ").trim()} ${file.widthMm}x${file.heightMm}${format === "sheet" ? " лист A4" : ""}.${format === "png" ? "png" : "pdf"}`;
        saveAs(await fetchCardFile(card._id, file._id, format), name);
    }, "Не удалось скачать файл");

    const copy = () => card && run("copy", async () => {
        const next = await duplicateCard(card._id);
        navigate(`${CARDS_ROOT}/${next._id}`);
    }, "Не удалось скопировать");

    const remove = () => {
        if (!card || busy || !window.confirm(`Удалить визитку «${card.name}»? Собранные файлы тоже удалятся.`)) return;
        run("delete", async () => {
            await deleteCard(card._id);
            leaveRef.current = true;
            navigate(CARDS_ROOT);
        }, "Не удалось удалить");
    };

    const pickLogo = (file) => {
        if (file.size > MAX_IMAGE_BYTES) {
            setMessage({ type: "error", text: "Файл больше 30 МБ." });
            return;
        }
        run("logo", async () => {
            const target = await persistProfile();
            const next = await uploadProfileLogo(target._id, file);
            if (mountedRef.current) setProfile((current) => ({ ...(current || target), hasLogo: next.hasLogo, logoUpdatedAt: next.logoUpdatedAt }));
        }, "Не удалось загрузить логотип");
    };
    const removeLogo = () => profile && run("logo", async () => {
        const next = await deleteProfileLogo(profile._id);
        if (mountedRef.current) setProfile((current) => ({ ...current, hasLogo: next.hasLogo, logoUpdatedAt: next.logoUpdatedAt }));
    }, "Не удалось убрать логотип");

    const hasUnsaved = (!!card && (cardDirty || profileDirty)) || (!card && profileDirty);
    useEffect(() => {
        if (!hasUnsaved) return undefined;
        const onBeforeUnload = (event) => {
            if (leaveRef.current) return;
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [hasUnsaved]);

    const goBack = () => {
        if (hasUnsaved && !window.confirm("Есть несохранённые правки. Выйти без сохранения?")) return;
        navigate(CARDS_ROOT);
    };

    const title = card ? card.name || "Визитка" : "Новая визитка";
    if (loading || (!profileDraft && !loadError)) {
        return <SignsPage title={title} wide onBack={goBack}><div className="signsEditor__placeholder"><Loader /></div></SignsPage>;
    }
    if (loadError) return <SignsPage title={title} wide onBack={goBack}><div className="signsEditor__placeholder">{loadError}</div></SignsPage>;

    const presets = (meta?.sizePresets || []).map((preset) => ({
        value: preset.key, label: preset.label, hint: preset.widthMm ? `${preset.widthMm} × ${preset.heightMm} мм` : "в мм",
    }));
    const designs = (meta?.designs || []).map((design) => ({ value: design.key, label: design.label, hint: design.hint }));
    const frontLayouts = (meta?.frontLayouts || []).map((item) => ({ value: item.key, label: item.label, hint: item.hint }));
    const backLayouts = (meta?.backLayouts || []).map((item) => ({ value: item.key, label: item.label, hint: item.hint }));
    const vertical = draft.size.orientation === "vertical";
    const sizeW = vertical ? draft.size.heightMm : draft.size.widthMm;
    const sizeH = vertical ? draft.size.widthMm : draft.size.heightMm;
    const last = card?.files?.[0];
    const selectedPart = selected.key ? draft[selected.side]?.parts?.[selected.key] : null;

    const actions = (
        <>
            <ActionsMenu
                label="Ещё"
                disabled={locked}
                items={[
                    { label: "Сделать копию", disabled: !card, onClick: copy },
                    profile && { label: "Открыть профиль бренда", onClick: () => navigate(`/admin/print-signs/profiles/${profile._id}`) },
                    { label: "Удалить визитку", danger: true, divider: true, disabled: !card, onClick: remove },
                ]}
            />
            <button type="button" className="signsButton" disabled={locked || (!dirty && !!card)} onClick={save}>
                {busy === "save" ? "Сохранение…" : dirty || !card ? "Сохранить" : "Сохранено"}
            </button>
            <button type="button" className="signsButton is-primary" disabled={locked} onClick={build}>
                {busy === "build" ? "Собираем…" : last ? "Собрать заново" : "Собрать PDF"}
            </button>
        </>
    );

    const renderSide = (sideKey, label) => {
        const data = preview[sideKey];
        return (
            <div className="signsCard__side">
                <div className="signsCard__sideTitle">{label}</div>
                <div className="signsCard__frame">
                    {data?.image ? (
                        <div className="signsCard__canvas">
                            <img src={data.image} alt={label} draggable={false} />
                            <CardCanvas
                                side={sideKey}
                                image={data.image}
                                layout={data.layout}
                                parts={draft[sideKey].parts}
                                selected={selected}
                                frames={frames}
                                loading={preview.loading}
                                disabled={locked}
                                onSelect={(sideName, key) => setSelected({ side: sideName, key })}
                                onPartChange={patchPart}
                            />
                        </div>
                    ) : (
                        <div className="signsEditor__stageEmpty">{preview.loading ? <Loader /> : "—"}</div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <SignsPage
            title={title}
            subtitle={card
                ? `${last ? "Собрана" : "Не собрана"} · ${sizeW} × ${sizeH} мм · изменил ${card.updatedByName || card.createdByName || "—"} · ${formatDateTime(card.updatedAt || card.createdAt)}`
                : "Визитка с двух сторон. Справа — сразу обе стороны, куски на них двигаются мышкой."}
            wide
            onBack={goBack}
            actions={actions}
        >
            <PageMessage message={message} onClose={() => setMessage(null)} />

            <div className="signsEditor">
                <div className="signsEditor__settings">
                    <Section title="Визитка">
                        <Field label="Название (для себя)" value={draft.name} maxLength={120} disabled={locked}
                            placeholder="Например: Роман — менеджер, Кировск" onChange={(name) => patch({ name })} />
                        <div className="printCatalogs__label">Профиль бренда — название, логотип, контакты фирмы</div>
                        <div className="printSigns__profileRow">
                            <select value={draft.profileId || ""} disabled={locked || profileLoading} onChange={(event) => changeProfile(event.target.value)}>
                                <option value="">{profiles.length ? "— Новый профиль (создастся при сохранении) —" : "Новый профиль (создастся при сохранении)"}</option>
                                {profiles.map((item) => <option key={item._id} value={item._id}>{item.name}{item.brandName && item.brandName !== item.name ? ` · ${item.brandName}` : ""}</option>)}
                            </select>
                        </div>
                        <p className="printCatalogs__hint">
                            {profile
                                ? "Данные фирмы правятся здесь и сохраняются в профиль — он общий для визиток и вывесок."
                                : "Профиля пока нет: на картинке заготовка. Впишите свои данные — при сохранении создастся профиль."}
                        </p>
                    </Section>

                    <Section title="Сотрудник" hint="Для личной визитки: ФИО, должность, свои телефоны и почты. Пусто — визитка фирмы с общими контактами.">
                        <div className="printCatalogs__grid2">
                            <Field label="ФИО" value={draft.person.name} maxLength={80} disabled={locked} placeholder="Иванов Иван"
                                onChange={(name) => patchNested("person", { name })} />
                            <Field label="Должность" value={draft.person.position} maxLength={80} disabled={locked} placeholder="Менеджер по заказам"
                                onChange={(position) => patchNested("person", { position })} />
                        </div>
                        <StringList label="Свои телефоны (вместо общих)" values={draft.person.phones} max={3} maxLength={60} disabled={locked}
                            placeholder="+7 (900) 111-11-11" addLabel="Телефон" onChange={(phones) => patchNested("person", { phones })} />
                        <StringList label="Свои почты (вместо общих)" values={draft.person.emails} max={2} disabled={locked}
                            placeholder="ivanov@likpamyati.ru" addLabel="Почту" onChange={(emails) => patchNested("person", { emails })} />
                    </Section>

                    <Section title="Лицевая сторона" hint="Логотип, название фирмы и подпись.">
                        <Choice options={frontLayouts} value={draft.front.layout} disabled={locked} onChange={(layout) => patchSide("front", { layout })} />
                        <LogoBox profile={profile} locked={locked} busy={busy === "logo"} onPick={pickLogo} onRemove={removeLogo} />
                        <div className="printCatalogs__grid2">
                            <Field label="Название фирмы" value={profileDraft.brandName} maxLength={80} disabled={locked}
                                onChange={(brandName) => patchProfile({ brandName })} />
                            <Field label="Подпись под названием" value={profileDraft.tagline} maxLength={80} disabled={locked}
                                onChange={(tagline) => patchProfile({ tagline })} />
                        </div>
                        <PartsEditor side="front" keys={FRONT_PARTS} parts={draft.front.parts} show={draft.front.show}
                            onChange={patchPart} onShow={patchShow} locked={locked}
                            unavailable={{ logo: profile?.hasLogo ? "" : "нет логотипа" }} />
                    </Section>

                    <Section title="Оборот" hint="Контакты и QR. Телефоны и почты — из профиля фирмы (или свои у сотрудника).">
                        <Choice options={backLayouts} value={draft.back.layout} disabled={locked} onChange={(layout) => patchSide("back", { layout })} />
                        <PartsEditor side="back" keys={BACK_PARTS} parts={draft.back.parts} show={draft.back.show}
                            onChange={patchPart} onShow={patchShow} locked={locked} />
                        <div className="printCatalogs__label">Что в контактах</div>
                        <div className="printCatalogs__switches">
                            {CONTACT_SHOW.map(([key, label]) => (
                                <Switch key={key} checked={draft.back.show[key] !== false} disabled={locked || draft.back.show.contacts === false} label={label}
                                    onChange={(value) => patchShow("back", key, value)} />
                            ))}
                        </div>
                        <StringList label="Телефоны фирмы" values={profileDraft.phones} max={8} maxLength={60} disabled={locked}
                            placeholder="+7 (900) 111-11-11" addLabel="Телефон" onChange={(phones) => patchProfile({ phones })} />
                        <StringList label="Почты фирмы" values={profileDraft.emails} max={6} disabled={locked}
                            placeholder="info@likpamyati.ru" addLabel="Почту" onChange={(emails) => patchProfile({ emails })} />
                        <StringList label="Адреса" values={profileDraft.addresses} max={4} maxLength={200} disabled={locked}
                            placeholder="г. Кировск, ул. Центральная, д. 10" addLabel="Адрес" onChange={(addresses) => patchProfile({ addresses })} />
                        <Field label="Сайт" value={profileDraft.site} maxLength={120} disabled={locked} placeholder="likpamyati.ru"
                            onChange={(site) => patchProfile({ site })} />
                        <div className="printCatalogs__grid2">
                            <Field label="Ссылка в QR (пусто — из профиля)" value={draft.qrUrl} maxLength={300} disabled={locked}
                                placeholder={profileDraft.qrUrl || profileDraft.site || "https://…"} onChange={(qrUrl) => patch({ qrUrl })} />
                            <Field label="Подпись у QR (пусто — из профиля)" value={draft.qrCaption} maxLength={120} disabled={locked}
                                placeholder={profileDraft.qrCaption || "Сканируйте…"} onChange={(qrCaption) => patch({ qrCaption })} />
                        </div>
                    </Section>

                    <Section title="Дизайн" hint="Те же оформления, что у вывесок, — визитка и вывеска будут в одном стиле.">
                        <Choice options={designs} value={draft.design} disabled={locked} onChange={(design) => patch({ design })} />
                        {draft.design === "custom" ? (
                            <ColorsEditor value={draft.colors} meta={meta} disabled={locked} onChange={(changes) => patchNested("colors", changes)} />
                        ) : null}
                        <Switch checked={draft.frame} disabled={locked} label="Тонкая рамка по краю" onChange={(frame) => patch({ frame })} />
                    </Section>

                    <Section title="Размер" hint="Стандарт в России — 90 × 50 мм, евро — 85 × 55 мм. Вылеты — запас фона за край под обрезку.">
                        <Choice options={presets} value={draft.size.preset} disabled={locked}
                            onChange={(preset) => {
                                const found = (meta?.sizePresets || []).find((item) => item.key === preset);
                                patchNested("size", found && found.widthMm ? { preset, widthMm: found.widthMm, heightMm: found.heightMm } : { preset });
                            }} />
                        {draft.size.preset === "custom" ? (
                            <div className="printCatalogs__range printSigns__sizeRow">
                                <span>Длина</span>
                                <input type="text" inputMode="numeric" value={draft.size.widthMm || ""} disabled={locked}
                                    onChange={(event) => patchNested("size", { widthMm: toSize(event.target.value) })} />
                                <span>× высота</span>
                                <input type="text" inputMode="numeric" value={draft.size.heightMm || ""} disabled={locked}
                                    onChange={(event) => patchNested("size", { heightMm: toSize(event.target.value) })} />
                                <span>мм</span>
                            </div>
                        ) : null}
                        <Choice options={ORIENTATIONS} value={draft.size.orientation} disabled={locked} onChange={(orientation) => patchNested("size", { orientation })} />
                        <div className="printCatalogs__label">Вылеты под обрезку</div>
                        <Choice options={BLEEDS} value={draft.size.bleedMm} disabled={locked} onChange={(bleedMm) => patchNested("size", { bleedMm })} />
                        <Switch checked={draft.size.cropMarks} disabled={locked} label="Метки реза в PDF для типографии"
                            onChange={(cropMarks) => patchNested("size", { cropMarks })} />
                    </Section>

                    <Section title="Фон" hint="Своя картинка на сторону (текстура, фото). Без неё — фон дизайна.">
                        {[["front", "Лицевая сторона"], ["back", "Оборот"]].map(([sideKey, label]) => (
                            <div key={sideKey} className="signsCard__bg">
                                <div className="printCatalogs__label">{label}</div>
                                <AssetPicker kind="background" assets={assets} value={draft[sideKey].background.assetId} disabled={locked}
                                    emptyLabel="Фон дизайна" setMessage={setMessage} onUploaded={(asset) => setAssets((current) => [asset, ...current])}
                                    onChange={(assetId) => patchSide(sideKey, { background: { ...draft[sideKey].background, assetId } })} />
                                {draft[sideKey].background.assetId ? (
                                    <ImageAdjust value={draft[sideKey].background} disabled={locked}
                                        onChange={(value) => patchSide(sideKey, { background: value })} />
                                ) : null}
                            </div>
                        ))}
                    </Section>

                    {card ? (
                        <Section title="Готовые файлы" hint="PDF — для типографии (лицо и оборот, с вылетами и метками реза). Лист A4 — напечатать в офисе с двух сторон. PNG — обе стороны для мессенджеров.">
                            {card.files.length ? (
                                <div className="signsFiles is-inline">
                                    {card.files.map((file, index) => (
                                        <div key={file._id} className="signsFiles__row">
                                            <div>
                                                <strong>{index === 0 ? <StatusBadge tone="success">последняя</StatusBadge> : null} {formatDateTime(file.createdAt)}</strong>
                                                <span>
                                                    {file.widthMm} × {file.heightMm} мм{file.bleedMm ? ` + вылеты ${file.bleedMm} мм` : ""} · PDF {formatBytes(file.pdfBytes)}
                                                    {file.perSheet ? ` · на A4 — ${file.perSheet} шт.` : ""}
                                                </span>
                                            </div>
                                            <div className="signsFiles__actions">
                                                <button type="button" className="signsButton" disabled={!!busy} onClick={() => download(file, "pdf")}>PDF</button>
                                                {file.hasSheet ? <button type="button" className="signsButton" disabled={!!busy} onClick={() => download(file, "sheet")}>Лист A4</button> : null}
                                                {file.hasPng ? <button type="button" className="signsButton" disabled={!!busy} onClick={() => download(file, "png")}>PNG</button> : null}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : <p className="printCatalogs__hint">Пока ни одной сборки — кнопка «Собрать PDF» сверху справа.</p>}
                        </Section>
                    ) : null}
                </div>

                <aside className="signsEditor__visual">
                    <div className="signsEditor__visualHead">
                        <div>
                            <strong>Обе стороны</strong>
                            <span>{sizeW} × {sizeH} мм{draft.size.bleedMm ? ` + вылеты ${draft.size.bleedMm} мм` : ""} · пунктир — линия реза</span>
                        </div>
                        <div className="signsEditor__visualTools">
                            <Switch checked={frames} onChange={setFrames} label="Рамки" />
                            <Switch checked={preview.auto} onChange={preview.setAuto} label="Обновлять сам" />
                            <button type="button" className="signsButton" disabled={preview.loading} onClick={preview.refresh}>
                                {preview.loading ? "Рисуем…" : "Обновить"}
                            </button>
                        </div>
                    </div>

                    {selectedPart ? (
                        <div className="signsEditor__blockTools is-header">
                            <i className="printSigns__blockDot is-header" />
                            <strong>{selected.side === "front" ? "Лицо" : "Оборот"} · {PART_LABELS[selected.key]}</strong>
                            <span>мышкой, стрелками или колесом</span>
                            <button type="button" className="signsButton" disabled={locked || selectedPart.s <= 0.3}
                                onClick={() => patchPart(selected.side, selected.key, { s: Math.max(0.3, Math.round((selectedPart.s - 0.1) * 100) / 100) })}>−</button>
                            <b className="signsCard__scale">{Math.round(selectedPart.s * 100)}%</b>
                            <button type="button" className="signsButton" disabled={locked || selectedPart.s >= 3}
                                onClick={() => patchPart(selected.side, selected.key, { s: Math.min(3, Math.round((selectedPart.s + 0.1) * 100) / 100) })}>+</button>
                            {[["←", -1, 0], ["→", 1, 0], ["↑", 0, -1], ["↓", 0, 1]].map(([arrow, dx, dy]) => (
                                <button key={arrow} type="button" className="signsButton" disabled={locked}
                                    onClick={() => patchPart(selected.side, selected.key, { x: nudge(selectedPart.x, dx * NUDGE), y: nudge(selectedPart.y, dy * NUDGE) })}>
                                    {arrow}
                                </button>
                            ))}
                            <button type="button" className="signsButton" disabled={locked || (selectedPart.s === 1 && !selectedPart.x && !selectedPart.y)}
                                onClick={() => patchPart(selected.side, selected.key, { s: 1, x: 0, y: 0 })}>↺ На место</button>
                            <button type="button" className="signsButton" onClick={() => setSelected({ side: "", key: "" })}>×</button>
                        </div>
                    ) : (
                        <p className="signsEditor__hint">Щёлкните кусок на визитке — логотип, название, ФИО, контакты или QR — и тяните его мышкой.</p>
                    )}

                    {preview.error ? <div className="printCatalogs__message is-error">{preview.error}</div> : null}
                    {preview.warnings?.length ? (
                        <div className="printCatalogs__message is-warning printSigns__warnings">
                            {preview.warnings.map((warning) => <div key={warning}>• {warning}</div>)}
                        </div>
                    ) : null}

                    <div className={`signsCard__stage${vertical ? " is-vertical" : ""}${preview.loading ? " is-loading" : ""}`}>
                        {renderSide("front", "Лицевая сторона")}
                        {renderSide("back", "Оборот")}
                    </div>
                </aside>
            </div>
        </SignsPage>
    );
};

/** Страница редактора: новая визитка после сохранения получает свой адрес, редактор не пересоздаётся. */
const PrintCardEditorPage = () => {
    const { cardId = "new" } = useParams();
    const navigate = useNavigate();
    const createdRef = useRef("");
    const key = cardId === "new" || cardId === createdRef.current ? "new" : cardId;
    return (
        <CardEditorView
            key={key}
            cardId={cardId}
            onCreated={(id) => {
                createdRef.current = id;
                navigate(`${CARDS_ROOT}/${id}`, { replace: true });
            }}
        />
    );
};

export default PrintCardEditorPage;
